import { APP_BASE_HREF } from '@angular/common';
import { EnvironmentProviders, inject, provideEnvironmentInitializer } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

/**
 * Configuration for a custom SVG icon to be registered with MatIconRegistry.
 */
export interface MatIconDefinition {
    /** The name used to reference the icon via `<mat-icon svgIcon="name">` */
    name: string;
    /** The path to the SVG file relative to assets folder (without leading slash) */
    assetPath: string;
}

/**
 * Available custom SVG icons that can be registered with MatIconRegistry.
 *
 * @description
 * These icons are registered lazily when the provider is included in a module/component.
 * Use them via `<mat-icon svgIcon="icon-name">`.
 *
 * @example
 * ```html
 * <mat-icon svgIcon="pt-undo"></mat-icon>
 * ```
 */
export const MAT_ICON_DEFINITIONS: MatIconDefinition[] = [
    // Practice Trainer toolbar icons
    {
        name: 'pt-undo',
        assetPath: 'assets/shared-ui-icons/icons/ic_undo.svg',
    },
    {
        name: 'pt-redo',
        assetPath: 'assets/shared-ui-icons/icons/ic_redo.svg',
    },
    {
        name: 'pt-rotate',
        assetPath: 'assets/shared-ui-icons/icons/ic_rotate.svg',
    },
    {
        name: 'pt-screenshot',
        assetPath: 'assets/shared-ui-icons/icons/ic_screenshot.svg',
    },
    {
        name: 'pt-layer',
        assetPath: 'assets/shared-ui-icons/icons/ic_layer.svg',
    },
    // Settings dialog icon
    {
        name: 'radio-button-checked',
        assetPath: 'assets/shared-ui-icons/icons/radio-button-checked-24x24.svg',
    },
    // Line style icons for brush style selector
    {
        name: 'pt-line-solid',
        assetPath: 'assets/shared-ui-icons/icons/ic_line_solid.svg',
    },
    {
        name: 'pt-line-dashed',
        assetPath: 'assets/shared-ui-icons/icons/ic_line_dashed.svg',
    },
    {
        name: 'pt-wallpaper-edit',
        assetPath: 'assets/shared-ui-icons/icons/wallpaper_edit.svg',
    },
    // Shape tool icons
    {
        name: 'pt-rectangle',
        assetPath: 'assets/shared-ui-icons/icons/ic_rectangle.svg',
    },
    {
        name: 'pt-ellipse',
        assetPath: 'assets/shared-ui-icons/icons/ic_ellipse.svg',
    },
    {
        name: 'pt-line',
        assetPath: 'assets/shared-ui-icons/icons/ic_line.svg',
    },
    // Arrow tool icon
    {
        name: 'pcp-arrow',
        assetPath: 'assets/shared-ui-icons/icons/ic_arrow.svg',
    },
    // Exam module: "Zurück zur Prüfungsfrage" in the workspace teacher-text panel
    {
        name: 'pcp-back-to-question',
        assetPath: 'assets/shared-ui-icons/icons/ic_back_to_question.svg',
    },
    // Exam page: driverscam marker on the "So geht Praxisprüfung" card
    {
        name: 'pcp-driverscam-square',
        assetPath: 'assets/shared-ui-icons/icons/ic_driverscam_square.svg',
    },
    // Next-slides-preview navigation toolbar
    {
        name: 'pcp-toolbar',
        assetPath: 'assets/shared-ui-icons/icons/ic_toolbar.svg',
    },
    {
        name: 'pcp-switch',
        assetPath: 'assets/shared-ui-icons/icons/ic_switch.svg',
    },
];

/**
 * Provides PCP custom SVG icons for MatIcon components.
 *
 * @description
 * Registers all icons defined in `MAT_ICON_DEFINITIONS` with Angular Material's
 * `MatIconRegistry`. Uses `provideEnvironmentInitializer` to support both eagerly
 * loaded and lazy-loaded modules.
 *
 * The provider respects `APP_BASE_HREF` for proper path resolution.
 *
 * **IMPORTANT**: Call this function **once at application level** in your app's
 * `main.ts` or `app.config.ts`. Do NOT call it in feature modules - icons are
 * application-level infrastructure, not feature-specific concerns.
 *
 * @example
 * ```typescript
 * // In app bootstrap (main.ts)
 * bootstrapApplication(AppComponent, {
 *     providers: [
 *         providePcpMatIcons(), // Register PCP custom SVG icons
 *         // ... other providers
 *     ]
 * });
 *
 * // In standalone app config (app.config.ts)
 * export const appConfig: ApplicationConfig = {
 *     providers: [
 *         providePcpMatIcons(),
 *         // ... other providers
 *     ],
 * };
 * ```
 *
 * @param icons - Optional array of icon definitions to register. Defaults to all icons.
 * @returns Environment providers that register the specified icons
 */
export function providePcpMatIcons(icons: MatIconDefinition[] = MAT_ICON_DEFINITIONS): EnvironmentProviders {
    return provideEnvironmentInitializer(() => {
        const iconRegistry = inject(MatIconRegistry);
        const sanitizer = inject(DomSanitizer);
        const baseHref = inject(APP_BASE_HREF);

        icons.forEach(icon => {
            const iconPath = `${baseHref}${icon.assetPath}`;
            iconRegistry.addSvgIcon(
                icon.name,
                sanitizer.bypassSecurityTrustResourceUrl(iconPath)
            );
        });

        iconRegistry
            // using material symbols, not material icons.
            .setDefaultFontSetClass('material-symbols-outlined', 'mat-ligature-font')
            .registerFontClassAlias(
                'material-symbols-outlined',
                'material-symbols-outlined mat-ligature-font',
            );
    });
}

/**
 * Provides a specific subset of PCP custom SVG icons.
 *
 * @description
 * Use this when you only need specific icons and want to minimize registration overhead.
 * Call at application level, not in feature modules.
 *
 * @example
 * ```typescript
 * // In app bootstrap - register only specific icons
 * bootstrapApplication(AppComponent, {
 *     providers: [
 *         providePcpMatIconsByName(['pt-undo', 'pt-redo'])
 *     ]
 * });
 * ```
 *
 * @param iconNames - Array of icon names to register
 * @returns Environment providers that register only the specified icons
 */
export function providePcpMatIconsByName(iconNames: string[]): EnvironmentProviders {
    const filteredIcons = MAT_ICON_DEFINITIONS.filter(icon => iconNames.includes(icon.name));
    return providePcpMatIcons(filteredIcons);
}

